import { Droppable, DragDropContext, Draggable } from "react-beautiful-dnd";
import { v4 as uuid } from "uuid";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../stores/hooks";
import { useDraggableInPortal } from "../../../hooks/use-draggable-in-portal";
import { useActionDragDrop } from "../../../hooks/use-action-drag-drop";
import { AddedEventButton } from "../../AddedEventButton";
import { SmallAddActionButton } from "../../SmallAddActionButton";
import * as A from "../../../stores/automation/mergers/actions";
import type {
  ActionCandidate,
  ActionGroupType,
} from "../../../types/candidate";
import type { BranchRef } from "../../../stores/automation/branches.store";
import type { CurrSection } from "../../../containers/AutomationForm/types";
import { triggersIdsAvailableForMergeRecordsAction } from "../../../stores/automation/utils/inputValidation";
import {
  activateNextSection,
  handleActionChange,
  showActionError,
  showActionFieldMissingError,
  showActionFieldsEmptyError,
} from "../../../utils/action-helpers";
import { BranchHeaderCard } from "./BranchHeaderCard";
import { BranchesBlockCard } from "./BranchesBlockCard";
import { NestedLoopGroup } from "./NestedLoopGroup";
import { NestedBranchesFan } from "./NestedBranchesFan";
import { StyledBranchColumn } from "./style";

interface BranchColumnProps {
  blockInstanceId: number;
  branchHash: string | "else";
  isSelected: boolean;
  /** Stable across renders (id-bound here, not in the parent) so the
   * column's React.memo isn't defeated by a fresh closure per render. */
  onSelect: (instanceId: number, branchHash: string) => void;
  activeAction: string | undefined;
  onClickAction: (hash?: string) => void;
  onClickTrigger: () => void;
  onChangeAction: (
    integrationId: string,
    actionId: string,
    actionGroupType?: ActionGroupType,
  ) => void;
  /** Kebab з пунктом Delete рендериться лише коли гілку МОЖНА видалити
   * (спека: остання гілка — контролі сховані). */
  isDeletable: boolean;
  /** Стабільний (useCallback у Sidebar) — після видалення переводить вибір
   * на панель блоку. */
  onBranchDeleted: (instanceId: number) => void;
  /** Nested Advanced Logic (Етап 7): вибір щойно доданого вкладеного CL-блоку
   * -- у Branches це прокинутий існуючий стабільний onSelectBlock. */
  onSelectNestedBlock: (instanceId: number) => void;
  /** Nested Advanced Logic (Етап 7): вибір щойно доданого вкладеного loop. */
  onSelectNestedLoop: (instanceId: number) => void;
  /** Nested Advanced Logic (Етап 7a): the currently active canvas section --
   * used to compute selection state for the nested block card/columns this
   * column self-recurses into (the root-level isSelected stays computed by
   * Branches, unchanged). */
  activeItem: CurrSection;
}

export const BranchColumn = observer<BranchColumnProps>(
  ({
    blockInstanceId,
    branchHash,
    isSelected,
    onSelect,
    activeAction,
    onClickAction,
    onClickTrigger,
    onChangeAction,
    isDeletable,
    onBranchDeleted,
    onSelectNestedBlock,
    onSelectNestedLoop,
    activeItem,
  }) => {
    const store = useRootStore();
    const renderDraggable = useDraggableInPortal();
    const candidate = store.automations.candidate;
    const branchesStore = candidate.branches;

    const ref: BranchRef = { instanceId: blockInstanceId, branchHash };
    const branch = branchesStore.findBranch(ref);
    const actions = branchesStore.branchActions(ref);
    const advancedGroup = branchesStore.branchAdvancedGroup(ref);

    const { handleDragStart, handleDragEnd } = useActionDragDrop({
      actions,
      onClickAction,
      setActions: (mergers) => branchesStore.setBranchActions(ref, ...mergers),
      setActionsWithOutReferences: (reference, whatActionReferencesRemove) =>
        candidate.setActionsWithOutReferences(
          reference,
          whatActionReferencesRemove,
        ),
    });

    if (branch === undefined) return null;

    const handleDelete = (
      e: React.MouseEvent,
      currActionHash: string,
    ): void => {
      e.stopPropagation();
      branchesStore.setBranchActions(
        ref,
        A.ejectActionReferences(currActionHash),
        A.ejectAction(currActionHash),
      );
      activateNextSection(
        currActionHash,
        branchesStore.branchActions(ref),
        onClickTrigger,
        onClickAction,
      );
    };

    const handleClickAction = (accessError?: boolean, hash?: string): void => {
      if (!accessError) onClickAction(hash);
    };

    const handleDuplicate = (hash: string): void => {
      branchesStore.setBranchActions(ref, A.mergeDuplicateAction(hash));
    };

    const showBranchActionErrors = (action: ActionCandidate): boolean => {
      const isSubmitted = candidate.isActionSubmitted(action.hash);

      if (!isSubmitted && candidate.isNewlyAddedAction(action)) {
        return false;
      }

      return (
        showActionFieldsEmptyError(action, isSubmitted) ||
        showActionFieldMissingError(
          store.automations.isAllDynamicDescriptionsLoaded(),
          candidate.self,
          action,
        )
      );
    };

    // Nested Advanced Logic (Етап 7/7a): after the nested advanced group
    // (block or loop) is deleted, fall back the active selection to THIS
    // column's own branch (the parent that held it) -- inline closure is
    // fine here, this stays inside the memo boundary. Shared by both the
    // "branches" and "loop" arms below since the fallback is identical.
    const handleNestedGroupDeleted = (): void => {
      onSelect(blockInstanceId, String(branchHash));
    };

    return (
      <StyledBranchColumn data-testid={`branch-column-${String(branchHash)}`}>
        <BranchHeaderCard
          blockInstanceId={blockInstanceId}
          branchHash={branchHash}
          isSelected={isSelected}
          onSelect={onSelect}
          isDeletable={isDeletable}
          onBranchDeleted={onBranchDeleted}
        />

        <DragDropContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Droppable
            droppableId={`branch-${String(branchHash)}`}
            isDropDisabled={actions.some((a) => a.deletedError)}
          >
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ position: "relative" }}
              >
                {actions.map((action, index) => (
                  <div key={action.hash}>
                    <Draggable
                      key={action.hash}
                      draggableId={action.hash}
                      index={index}
                    >
                      {renderDraggable((provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <AddedEventButton
                            groupType="branches"
                            eventType="action"
                            key={index}
                            isHighlighted={activeAction === action.hash}
                            isActive={activeAction === action.hash}
                            action={action}
                            error={showActionError(
                              action,
                              candidate.actionsSubmitted,
                              triggersIdsAvailableForMergeRecordsAction,
                            )}
                            fieldMissingError={showBranchActionErrors(action)}
                            disabled={
                              (action.accessError ?? false) ||
                              (action.integrationDisabled ?? false) ||
                              (action.actionDisabled ?? false)
                            }
                            onClick={() => {
                              handleClickAction(
                                action.accessError,
                                action.hash,
                              );
                            }}
                            onDuplicate={handleDuplicate}
                            onDelete={(e, currActionHash) => {
                              handleDelete(e, currActionHash);
                            }}
                            onChange={(integrationId, actionId) => {
                              handleActionChange(
                                store,
                                integrationId,
                                actionId,
                                "branches",
                                onChangeAction,
                                onClickAction,
                              );
                            }}
                          />
                        </div>
                      ))}
                    </Draggable>
                  </div>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {advancedGroup?.config.oneofKind === "branches" && (
          <BranchesBlockCard
            instanceId={advancedGroup.instanceId}
            isActive={
              activeItem?.type === "branches" &&
              activeItem.instanceId === advancedGroup.instanceId
            }
            onSelect={onSelectNestedBlock}
            onDeleted={handleNestedGroupDeleted}
          />
        )}

        {advancedGroup?.config.oneofKind === "loop" && (
          <NestedLoopGroup
            instanceId={advancedGroup.instanceId}
            isActive={
              activeItem?.type === "nested-loop" &&
              activeItem.instanceId === advancedGroup.instanceId
            }
            onSelect={onSelectNestedLoop}
            onDeleted={handleNestedGroupDeleted}
            activeAction={activeAction}
            onClickAction={onClickAction}
            onClickTrigger={onClickTrigger}
            onChangeAction={onChangeAction}
            activeItem={activeItem}
            onSelectBranch={onSelect}
            onSelectNestedBlock={onSelectNestedBlock}
          />
        )}

        <SmallAddActionButton
          fullWidth
          disabled={
            candidate.totalStepCount >=
              store.featureFlags.limits.maxActionsPerAutomation ||
            !candidate.self.trigger.triggerReference?.triggerId
          }
          advancedLogic={{
            looping: branchesStore.canAddLoopAt(ref),
            branches: branchesStore.canAddBlockAt(ref),
          }}
          onClick={(integrationId, actionId) => {
            if (integrationId === "branches") {
              const id = branchesStore.addBlockAt(ref);
              // auto-select the new nested block -> deep block panel works
              // the same for nested blocks as it does for root ones
              if (id !== undefined) onSelectNestedBlock(id);
              return;
            }
            if (integrationId === "looping") {
              const id = branchesStore.addLoopAt(ref);
              if (id !== undefined) onSelectNestedLoop(id);
              return;
            }
            const hash = uuid();
            branchesStore.setBranchActions(
              ref,
              A.mergeEmptyAction(hash, integrationId, actionId),
            );
            onClickAction(hash);
          }}
        />

        {advancedGroup?.config.oneofKind === "branches" && (
          <NestedBranchesFan
            instanceId={advancedGroup.instanceId}
            activeItem={activeItem}
            activeAction={activeAction}
            onSelect={onSelect}
            onClickAction={onClickAction}
            onClickTrigger={onClickTrigger}
            onChangeAction={onChangeAction}
            onSelectNestedBlock={onSelectNestedBlock}
            onSelectNestedLoop={onSelectNestedLoop}
          />
        )}
      </StyledBranchColumn>
    );
  },
);
BranchColumn.displayName = "BranchColumn";
